import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Formik } from 'formik'
import { useState } from 'react'
import { ActionButton } from '../../../utils/ui_utils'

// Single setting row: title + helper text on the left, control on the right.
const SettingRow = ({ title, description, control, alignTop = false }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      justifyContent: 'space-between',
      alignItems: { xs: 'stretch', sm: alignTop ? 'flex-start' : 'center' },
      gap: 2,
      py: 2,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0, pr: { sm: 2 } }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    <Box sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: alignTop ? 'flex-start' : 'center' } }}>
      {control}
    </Box>
  </Box>
)

// Section card with subtle border, title header and divider above rows.
const SettingSection = ({ title, children, sx }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, ...sx }}>
    <CardHeader
      title={title}
      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
      sx={{ py: 2, px: 3, '& .MuiCardHeader-content': { minWidth: 0 } }}
    />
    <Divider />
    <CardContent sx={{ px: 3, py: 0, '&:last-child': { pb: 1 } }}>{children}</CardContent>
  </Card>
)

function TerminalSettingsComponent({ okText, onSubmit, initialValues, onUpdateAll }) {
  if (initialValues == undefined) {
    initialValues = {
      bill_reset_status: true,
      bill_save_status: false,
      token_by_printer_num: false,
      duplicate_bill_status: false,
      open_price_status: false,
      gst_status: false,
      guest_status: false,
      hf_status: false,
      kot_status: false,
      kot_token_delete: false,
      logo_status: false,
      reverse_gst_status: false,
      roundoff: true,
      token_logo_status: false,
      tokenhf_status: false,
      waiter_status: false,
      parcel_status: 0,
      kottoken_status: 2,
      inventory_status: false,
      bill_without_stock_status: true,
      bill_settlement_status: false,
      udn_commission_status: 0,
    }
  }
  if (initialValues.udn_commission_status === undefined || initialValues.udn_commission_status === null) {
    initialValues.udn_commission_status = 0
  }

  if (initialValues?.parcel_status == true) initialValues.parcel_status = 1
  else if (initialValues.parcel_status == false) initialValues.parcel_status = 0
  else initialValues.parcel_status = 2

  if (initialValues.open_price_status == undefined) initialValues.open_price_status = false
  if (initialValues.duplicate_bill_status == undefined) initialValues.duplicate_bill_status = false

  if (initialValues.kottoken_status == true) initialValues.kottoken_status = 1
  else if (initialValues.kottoken_status == false) initialValues.kottoken_status = 0
  else initialValues.kottoken_status = 2

  const handleSubmit = async (values) => {
    onSubmit(values)
  }

  const handleUpdateAll = async (values) => {
    onUpdateAll(values)
  }

  const PerQtyMsg = 'Considers Parcel Charges mentioned below for each qty of item.'
  const PerItemMsg = 'Considers Parcel Amount mentioned in item menu details.'
  const FixedChargeMsg = 'Considers Parcel Charges mentioned below as Fixed amount for the bill.'
  const tokenPrintModeKitchenCounterDescription = 'Prints token by grouping Kitchen Counters.'
  const tokenPrintModePrinterNumberDescription = 'Prints token by grouping Printer Number.'
  const [tokenMsg, setTokenMsg] = useState(
    initialValues.token_by_printer_num
      ? tokenPrintModePrinterNumberDescription
      : tokenPrintModeKitchenCounterDescription
  )
  const [msg, setMsg] = useState(
    initialValues.parcel_status == 0 ? PerQtyMsg : initialValues.parcel_status == 1 ? PerItemMsg : FixedChargeMsg
  )

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ errors, handleBlur, handleChange, handleSubmit, setFieldValue, touched, values }) => {
        // Reusable Switch control bound to a Formik field
        const fieldSwitch = (name, extra = {}) => (
          <Switch
            name={name}
            checked={!!values[name]}
            onChange={extra.onChange || handleChange}
            disabled={extra.disabled}
          />
        )

        return (
          <form onSubmit={handleSubmit}>
            {/* Action bar — single, top-right, sticky inside the page scroll area */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 5,
                backgroundColor: 'background.default',
                py: 1.5,
                mb: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
              }}
            >
              <ActionButton variant="outlined" size="medium" color="info" onClick={() => handleUpdateAll(values)}>
                Update All
              </ActionButton>
              <ActionButton variant="contained" size="medium" color="info" type="submit">
                {okText}
              </ActionButton>
            </Box>

            <Stack spacing={3}>
              {/* ── Bill & Print Settings ─────────────────────────────────── */}
              <SettingSection title="Bill & Print">
                <Stack divider={<Divider flexItem />}>
                  <SettingRow
                    title="Prompt Before Printing"
                    description="Can skip printing during billing."
                    control={fieldSwitch('bill_save_status')}
                  />
                  <SettingRow
                    title="Header & Footer"
                    description="Print header and footer on the bill."
                    control={fieldSwitch('hf_status')}
                  />
                  <SettingRow
                    title="Logo"
                    description="Print logo on bill."
                    control={fieldSwitch('logo_status')}
                  />
                  <SettingRow
                    title="Round Off"
                    description="Rounds off the total amount to 50 paise."
                    control={fieldSwitch('roundoff')}
                  />
                  <SettingRow
                    title="Bill Reset"
                    description="Resets the bill number from 1 daily."
                    control={fieldSwitch('bill_reset_status')}
                  />
                  <SettingRow
                    title="Duplicate Bill"
                    description="Allows the user to print a duplicate copy of the current bill."
                    control={fieldSwitch('duplicate_bill_status')}
                  />
                </Stack>
              </SettingSection>

              {/* ── Pricing & Quantity ────────────────────────────────────── */}
              <SettingSection title="Pricing & Quantity">
                <Stack divider={<Divider flexItem />}>
                  <SettingRow
                    title="Service Charge"
                    description="Applies a service charge on the bill (in %)."
                    alignTop
                    control={
                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          size="small"
                          name="servicecharge"
                          label="Service Charge"
                          value={values?.servicecharge}
                          error={Boolean(touched.servicecharge && errors.servicecharge)}
                          helperText={touched.servicecharge && errors.servicecharge}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          variant="outlined"
                          type="number"
                          sx={{ width: 160 }}
                          disabled={!values.servicecharge_status}
                        />
                        {fieldSwitch('servicecharge_status')}
                      </Stack>
                    }
                  />
                  <SettingRow
                    title="Quantity Window"
                    description="Allows adding quantity for an item."
                    control={fieldSwitch('quantitywindow_status', {
                      disabled: values.open_price_status,
                    })}
                  />
                  <SettingRow
                    title="Open Price"
                    description="Allows editing the item price during billing."
                    control={fieldSwitch('open_price_status', {
                      onChange: (event) => {
                        handleChange(event)
                        if (event.target.checked) {
                          setFieldValue('quantitywindow_status', true)
                        }
                      },
                    })}
                  />
                </Stack>
              </SettingSection>

              {/* ── Parcel Charges ────────────────────────────────────────── */}
              <SettingSection title="Parcel Charges">
                <Box sx={{ py: 2 }}>
                  <FormControl>
                    <RadioGroup
                      row
                      name="parcel_status"
                      value={values.parcel_status}
                      onChange={(event) => {
                        const v = event.target.value
                        if (v == 0) setMsg(PerQtyMsg)
                        else if (v == 1) setMsg(PerItemMsg)
                        else if (v == 2) setMsg(FixedChargeMsg)
                        setFieldValue('parcel_status', v)
                      }}
                    >
                      <FormControlLabel value={1} control={<Radio />} label="Per Item" />
                      <FormControlLabel value={2} control={<Radio />} label="Fixed Charge" />
                      <FormControlLabel value={0} control={<Radio />} label="Per Qty" />
                    </RadioGroup>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                    {msg}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        name="parcel_amount"
                        label="Parcel Amount"
                        value={values?.parcel_amount}
                        error={Boolean(touched.parcel_amount && errors.parcel_amount)}
                        helperText={touched.parcel_amount && errors.parcel_amount}
                        onBlur={handleBlur}
                        onChange={(event) => {
                          const v = event.target.value
                          setFieldValue('parcel_amount', v < 0 ? 0 : +v)
                        }}
                        variant="outlined"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        name="parcel_cgst"
                        label="Parcel CGST"
                        value={values?.parcel_cgst}
                        error={Boolean(touched.parcel_cgst && errors.parcel_cgst)}
                        helperText={touched.parcel_cgst && errors.parcel_cgst}
                        onBlur={handleBlur}
                        onChange={(event) => {
                          const v = event.target.value
                          setFieldValue('parcel_cgst', v < 0 ? 0 : +v)
                        }}
                        variant="outlined"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        name="parcel_sgst"
                        label="Parcel SGST"
                        value={values?.parcel_sgst}
                        error={Boolean(touched.parcel_sgst && errors.parcel_sgst)}
                        helperText={touched.parcel_sgst && errors.parcel_sgst}
                        onBlur={handleBlur}
                        onChange={(event) => {
                          const v = event.target.value
                          setFieldValue('parcel_sgst', v < 0 ? 0 : +v)
                        }}
                        variant="outlined"
                        type="number"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </SettingSection>

              {/* ── GST Settings ──────────────────────────────────────────── */}
              <SettingSection title="GST">
                <Stack divider={<Divider flexItem />}>
                  <SettingRow
                    title="GST"
                    description="Considers GST calculation during the bill."
                    control={fieldSwitch('gst_status', {
                      onChange: (e) => {
                        handleChange(e)
                        setFieldValue('reverse_gst_status', false)
                      },
                    })}
                  />
                  <SettingRow
                    title="Reverse GST"
                    description="Considers reverse GST calculation."
                    control={fieldSwitch('reverse_gst_status', { disabled: !values.gst_status })}
                  />
                </Stack>
              </SettingSection>

              {/* ── KOT Settings ──────────────────────────────────────────── */}
              <SettingSection title="KOT & Service">
                <Stack divider={<Divider flexItem />}>
                  <SettingRow
                    title="Waiter"
                    description="Allows selecting waiter details in table orders."
                    control={fieldSwitch('waiter_status', {
                      onChange: (e) => {
                        handleChange(e)
                        if (!e.target.checked) {
                          setFieldValue('udn_commission_status', 0)
                        }
                      },
                    })}
                  />
                  <SettingRow
                    title="Commission Mode"
                    description={
                      !values.waiter_status
                        ? 'Waiter commission is disabled, so it will not be tracked.'
                        : values.udn_commission_status === 1
                          ? 'Commission is calculated per item based on its assigned percentage.'
                          : values.udn_commission_status === 2
                            ? 'Waiter commission is applied to bill amount (excluding GST & cess) by the provided commission %.'
                            : 'Waiter commission is disabled, so it will not be tracked.'
                    }
                    control={
                      <TextField
                        select
                        size="small"
                        name="udn_commission_status"
                        value={values.waiter_status ? values.udn_commission_status ?? 0 : 0}
                        onChange={handleChange}
                        disabled={!values.waiter_status}
                        variant="outlined"
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value={0}>Disabled</MenuItem>
                        <MenuItem value={1}>Item-Sale</MenuItem>
                        <MenuItem value={2}>Value-Sale</MenuItem>
                      </TextField>
                    }
                  />
                  <SettingRow
                    title="Guest"
                    description="Allows setting guest count during a table order."
                    control={fieldSwitch('guest_status')}
                  />
                  <SettingRow
                    title="KOT Token Header & Footer"
                    description="Print header and footer on the KOT token."
                    control={fieldSwitch('tokenhf_status')}
                  />
                  <SettingRow
                    title="KOT Token Logo"
                    description="Prints logo on KOT token."
                    control={fieldSwitch('token_logo_status')}
                  />
                  <SettingRow
                    title="KOT Token Item Delete"
                    description="Allows the cashier to delete items added in a KOT token."
                    control={fieldSwitch('kot_token_delete')}
                  />
                  <SettingRow
                    title="Token Print Mode"
                    description={tokenMsg}
                    control={fieldSwitch('token_by_printer_num', {
                      onChange: (event) => {
                        handleChange(event)
                        setTokenMsg(
                          event.target.checked
                            ? tokenPrintModePrinterNumberDescription
                            : tokenPrintModeKitchenCounterDescription
                        )
                      },
                    })}
                  />
                  <SettingRow
                    title="Token"
                    description="This option allows printing the token along with the bill."
                    alignTop
                    control={
                      <RadioGroup
                        row
                        name="kottoken_status"
                        value={values.kottoken_status}
                        onChange={(event) => setFieldValue('kottoken_status', +event.target.value)}
                      >
                        <FormControlLabel value={1} control={<Radio />} label="Enable" />
                        <FormControlLabel value={2} control={<Radio />} label="Only KOT" />
                        <FormControlLabel value={0} control={<Radio />} label="Disable" />
                      </RadioGroup>
                    }
                  />
                  <SettingRow
                    title="Bill Settlement"
                    description="Enables the bill settlement feature for KOT bills."
                    control={fieldSwitch('bill_settlement_status')}
                  />
                </Stack>
              </SettingSection>

              {/* ── Inventory ─────────────────────────────────────────────── */}
              <SettingSection title="Inventory">
                <Stack divider={<Divider flexItem />}>
                  <SettingRow
                    title="Inventory Status"
                    description="Enables maintaining inventory for items."
                    control={fieldSwitch('inventory_status')}
                  />
                </Stack>
              </SettingSection>
            </Stack>

            {/* Bottom action bar */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
                mt: 4,
                pt: 3,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <ActionButton variant="outlined" size="medium" color="info" onClick={() => handleUpdateAll(values)}>
                Update All
              </ActionButton>
              <ActionButton variant="contained" size="medium" color="info" type="submit">
                {okText}
              </ActionButton>
            </Box>
          </form>
        )
      }}
    </Formik>
  )
}

export default TerminalSettingsComponent
